import { axiosAdmin } from './axios';
import { IListResponse } from '../interfaces/listResponse';
import { ICustomerFullProfile } from '../interfaces/customer';
import { IDocumentUpload } from '../interfaces/documentUpload';
import { ICertificate, ICertificateUpload } from '../interfaces/certificate';
import { AxiosResponse } from 'axios';
import { IEducation } from '../interfaces/education';
import { IExperience } from '../interfaces/experience';
import { IAvailability } from '../interfaces/availability';
import { ILanguage } from '../interfaces/language';
import { IAdminUpdateFreelancerProfile } from '../interfaces/adminUpdateFreelancerProfile';
import { IFreelancerSearchRequest, IFreelancerSearchResponse } from '../interfaces/freelancerSearch';
import { IBlobResponse } from '../interfaces/blobResponse';

export namespace FreelancerService {
  const baseUrl = '/freelancers';

  const getFileDataFromBlobResponse = (res: AxiosResponse<Blob>): { src: string, name: string } => {
    const contentDisposition = res.headers['content-disposition']?.split(`''`) || [];
    const name = decodeURIComponent(contentDisposition[contentDisposition.length - 1]);
    const src = URL.createObjectURL(res.data);
    return { src, name };
  };

  export function getResume(id: number): Promise<IBlobResponse> {
    return axiosAdmin.get<Blob>(baseUrl + `/${id}/resume/download`, { responseType: 'blob' }).then(getFileDataFromBlobResponse);
  }

  export function uploadResume(id: number, body: IDocumentUpload): Promise<any> {
    return axiosAdmin.post(baseUrl + `/${id}/resume/upload`, body);
  }

  export function getList(search: IFreelancerSearchRequest): Promise<IListResponse<IFreelancerSearchResponse>> {
    return axiosAdmin.post<IListResponse<IFreelancerSearchResponse>>(baseUrl + '/search', search)
      .then(({ data }) => data);
  }

  export function getOneById(id: number): Promise<ICustomerFullProfile> {
    return axiosAdmin.get(`${baseUrl}/${id}`)
      .then(({ data }) => data);
  };

  export function uploadCertificate(id: number, certificate: ICertificateUpload): Promise<ICertificate> {
    return axiosAdmin.post<ICertificate>(baseUrl + `/${id}/certificates`, certificate).then(({ data }) => data);
  }

  export function editCertificate(id: number, certificate: ICertificateUpload): Promise<ICertificate> {
    return axiosAdmin.patch<ICertificate>(baseUrl + `/${id}/certificates/${certificate.id}`, certificate).then(({ data }) => data);
  }

  export function getCertificate(id: number, certId: string): Promise<IBlobResponse> {
    return axiosAdmin.get<Blob>(baseUrl + `/${id}/certificates/${certId}`, { responseType: 'blob' }).then(getFileDataFromBlobResponse);
  }

  export function deleteCertificate(id: number, certId: string): Promise<ICertificate> {
    return axiosAdmin.delete<ICertificate>(baseUrl + `/${id}/certificates/${certId}`).then(({ data }) => data);
  }

  export function addEducation(id: number, education: Omit<IEducation, 'id'>): Promise<IEducation> {
    return axiosAdmin.post<IEducation>(baseUrl + `/${id}/education`, education).then(({ data }) => data);
  }

  export function editEducation(id: number, education: Partial<IEducation> & { id: string }): Promise<IEducation> {
    return axiosAdmin.patch<IEducation>(baseUrl + `/${id}/education/${education.id}`, education).then(({ data }) => data);
  }

  export function deleteEducation(id: number, educationId: string): Promise<IEducation> {
    return axiosAdmin.delete<IEducation>(baseUrl + `/${id}/education/${educationId}`).then(({ data }) => data);
  }

  export function addExperience(id: number, experience: Omit<IExperience, 'id'>): Promise<IExperience> {
    return axiosAdmin.post<IExperience>(baseUrl + `/${id}/experience`, experience).then(({ data }) => data);
  }

  export function editExperience(id: number, experience: Partial<IExperience> & { id: string }): Promise<IExperience> {
    return axiosAdmin.patch<IExperience>(baseUrl + `/${id}/experience/${experience.id}`, experience).then(({ data }) => data);
  }

  export function deleteExperience(id: number, expId: string): Promise<IExperience> {
    return axiosAdmin.delete<IExperience>(baseUrl + `/${id}/experience/${expId}`).then(({ data }) => data);
  }

  export function saveAvailability(id: number, availability: IAvailability): Promise<IAvailability> {
    return axiosAdmin.post<IAvailability>(baseUrl + `/${id}/availability`, availability).then(({ data }) => data);
  }

  export function saveLanguage(id: number, language: ILanguage): Promise<ILanguage> {
    return axiosAdmin.post<ILanguage>(baseUrl + `/${id}/languages`, language).then(({ data }) => data);
  }

  export function editLanguage(id: number, language: Partial<ILanguage> & { id: string }): Promise<ILanguage> {
    return axiosAdmin.patch<ILanguage>(baseUrl + `/${id}/languages/` + language.id, language).then(({ data }) => data);
  }

  export function deleteLanguage(id: number, langId: string): Promise<ILanguage> {
    return axiosAdmin.delete<ILanguage>(baseUrl + `/${id}/languages/${langId}`).then(({ data }) => data);
  }

  export function editProfile(id: number, update: IAdminUpdateFreelancerProfile): Promise<ICustomerFullProfile> {
    return axiosAdmin.post<ICustomerFullProfile>(baseUrl + `/${id}/profile`, update).then(({ data }) => data);
  }

  export function addSkills(id: number, skills: string[]): Promise<''> {
    return axiosAdmin.post<''>(baseUrl + `/${id}/skills`, { skills }).then(({ data }) => data);
  }

  export function removeSkills(id: number, skills: string[]): Promise<''> {
    return axiosAdmin.delete<''>(baseUrl + `/${id}/skills`, { data: { skills } }).then(({ data }) => data);
  }

}